import { Value } from '@/component/Value';
import { styled } from '@mui/material/styles';
import MuiTableFooter from '@mui/material/TableFooter';
import TableRow from '@mui/material/TableRow';
import type { Column, Table } from '@tanstack/react-table';
import { BodyCell } from '../components/BodyCell';
import { useDataProvider } from '../DataProviderContext';
import { useAggregation } from './AggregationContext';
import type { AggregationFn, AggregationType } from './types';

const Cell = styled(BodyCell, { name: 'TableFooter', slot: 'Cell' })(
  ({ theme }) => ({
    fontSize: 14,
    fontWeight: 700,
    color: theme.palette.text.primary,
  })
);

export const FlatTableAggregations = () => {
  const { table, isLoading } = useDataProvider();
  const aggregation = useAggregation();

  if (!table || isLoading || aggregation === undefined) {
    return null;
  }

  const { aggregateFn, showTypes } = aggregation;
  const columns = table.getVisibleFlatColumns();
  const isEnabled = columns.some(
    (column) => column.columnDef.meta?.enableAggregations
  );

  if (!isEnabled && showTypes.length > 0) {
    return null;
  }

  return (
    <MuiTableFooter>
      {showTypes.map((type) => {
        return (
          <TableRow key={type}>
            {columns.map((column) => (
              <Cell key={column.id}>
                <Aggregation
                  type={type}
                  column={column}
                  table={table}
                  aggregateFn={aggregateFn}
                />
              </Cell>
            ))}
          </TableRow>
        );
      })}
    </MuiTableFooter>
  );
};

const Aggregation = ({
  type,
  column,
  table,
  aggregateFn,
}: {
  type: AggregationType;
  column: Column<any>;
  table: Table<any>;
  aggregateFn: AggregationFn<any>;
}) => {
  if (!column.columnDef.meta?.enableAggregations) {
    return null;
  }
  const value = aggregateFn(type, { column, table });
  // @ts-ignore
  const Component = column.columnDef.Aggregation;

  if (Component) {
    return <Component value={value} type={type} columns={column} />;
  }

  return (
    <>
      <Value value={value} /> ({type.toUpperCase()})
    </>
  );
};
